import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { useDropzone } from "react-dropzone";
import {
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Divider,
  CircularProgress,
  MenuItem,
  Autocomplete,
  Alert,
  Tooltip,
  Select,
} from "@mui/material";
import {
  Add,
  Delete,
  Storefront,
  Description,
  FormatListBulleted,
  UploadFile,
  InfoOutlined,
  InsertDriveFile,
  Visibility,
  CheckCircle,
} from "@mui/icons-material";
import userService from "../../services/userService";
import hsCodeService from "../../services/hsCodeService";
import documentService from "../../services/documentService";
import { selectCurrencyOptions } from "../../redux/slices/currenciesSlice";
import CurrencyFlag from "../common/CurrencyFlag";
import { getCurrencyCountry } from "../../utils/currencyCountries";

// Local-only draft autosave — e.g. a customer hits the "add a TIN first"
// error, has to navigate away to Profile to fix it, and previously lost
// everything they'd typed. Scoped per-customer (shared browser/device
// shouldn't leak one customer's in-progress draft to another) and only
// for NEW shipments — editing an existing one always starts from the
// real server data, never a stale local draft.
const draftStorageKey = (customerId) => (customerId ? `shipmentDraft_${customerId}` : null);

const loadDraft = (customerId) => {
  const key = draftStorageKey(customerId);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveDraft = (customerId, draft) => {
  const key = draftStorageKey(customerId);
  if (!key) return;
  try {
    localStorage.setItem(key, JSON.stringify(draft));
  } catch {
    // Storage full/unavailable (private browsing, etc.) — the draft is a
    // convenience, not a requirement, so just drop it silently.
  }
};

const clearDraft = (customerId) => {
  const key = draftStorageKey(customerId);
  if (!key) return;
  try {
    localStorage.removeItem(key);
  } catch {
    // no-op
  }
};

const EMPTY_ITEM = {
  description: "",
  trackingNumber: "",
  quantity: 1,
  hsCode: "",
  hsCodeDescription: "",
};

// Values match server's normalizeCarrier() exactly (server/controllers/
// shipmentController.js) — it always upper-cases whatever's submitted to
// one of these keys, so a saved shipment's carrier is always one of these
// exact strings, not the friendlier display label.
const CARRIER_OPTIONS = [
  { value: "", label: "Select carrier…" },
  { value: "UPS", label: "UPS" },
  { value: "FEDEX", label: "FedEx" },
  { value: "USPS", label: "USPS" },
  { value: "DHL", label: "DHL" },
  { value: "AMAZON", label: "Amazon Logistics" },
  { value: "OTHER", label: "Other" },
];

// Common store/vendor names offered as autocomplete suggestions on the
// "Store / Vendor Name" field — plain free text either way, this is purely
// a typing shortcut, not a validated/whitelisted value.
const VENDOR_NAME_SUGGESTIONS = [
  "Amazon",
  "eBay",
  "SHEIN",
  "Fashion Nova",
  "Walmart",
  "Target",
  "Temu",
  "AliExpress",
  "Nike",
  "Adidas",
  "Zara",
  "H&M",
  "Home Depot",
  "Lowe's",
  "Best Buy",
  "Apple",
  "Etsy",
  "Costco",
];

const SectionHeader = ({ icon, number, title, subtitle }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2.5 }}>
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.5,
        bgcolor: "info.light",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        color: "primary.main",
      }}
    >
      {icon}
    </Box>
    <Box>
      <Typography fontWeight={700} fontSize={14}>
        {number}. {title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {subtitle}
      </Typography>
    </Box>
  </Box>
);

const ShipmentForm = ({
  open,
  onClose,
  onSubmit,
  initialData = null,
  loading = false,
  isAdmin = false,
  currentUser = null,
  activeTin = null,
}) => {
  const isEdit = !!initialData;

  const [form, setForm] = useState({
    vendorName: "",
    vendorWebsite: "",
    vendorCountry: "USA",
    orderNumber: "",
    carrier: "",
    providerTrackingNumber: "",
    packageDescription: "",
    items: [{ ...EMPTY_ITEM }],
  });
  const [pendingFiles, setPendingFiles] = useState([]);
  const [docType, setDocType] = useState("invoice");
  const [invoiceTotal, setInvoiceTotal] = useState("");
  // What currency the customer is actually typing invoiceTotal in — sent to
  // the server as-is and converted to USD there ONCE, at creation time (see
  // server/services/foreignCurrencyService.js). Defaults to USD, the only
  // currency Shipment.invoiceTotal is ever allowed to hold.
  const [invoiceCurrency, setInvoiceCurrency] = useState("USD");
  const currencyOptions = useSelector(selectCurrencyOptions);
  const [tin, setTin] = useState("");

  // Admin-only: customer picker (debounced server search)
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerLoading, setCustomerLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerInput, setCustomerInput] = useState("");

  const [hsOptions, setHsOptions] = useState({}); // { [idx]: [...codes] }
  const [hsLoading, setHsLoading] = useState({});
  const [hsInputs, setHsInputs] = useState({}); // { [idx]: inputText }

  // Store/vendor name suggestions — debounced 500ms so the dropdown doesn't
  // recompute on every keystroke.
  const [vendorSuggestions, setVendorSuggestions] = useState([]);
  useEffect(() => {
    const query = form.vendorName.trim().toLowerCase();
    if (!query) {
      setVendorSuggestions([]);
      return;
    }
    const timer = setTimeout(() => {
      setVendorSuggestions(VENDOR_NAME_SUGGESTIONS.filter((v) => v.toLowerCase().includes(query)));
    }, 500);
    return () => clearTimeout(timer);
  }, [form.vendorName]);

  // If what's typed matches a known vendor case-insensitively but wasn't
  // picked from the dropdown (e.g. "amazon" typed then tabbed away),
  // snap it to the canonical spelling/casing once the field loses focus.
  const handleVendorNameBlur = () => {
    const typed = form.vendorName.trim();
    if (!typed) return;
    const match = VENDOR_NAME_SUGGESTIONS.find((v) => v.toLowerCase() === typed.toLowerCase());
    if (match && match !== typed) {
      setForm((p) => ({ ...p, vendorName: match }));
    }
  };

  useEffect(() => {
    if (!isAdmin) return;

    const timers = {};
    Object.entries(hsInputs).forEach(([idx, query]) => {
      const trimmed = (query || "").trim();
      if (trimmed.length < 2) {
        setHsOptions((p) => ({ ...p, [idx]: [] }));
        return;
      }
      setHsLoading((p) => ({ ...p, [idx]: true }));
      timers[idx] = setTimeout(() => {
        hsCodeService
          .search(trimmed)
          .then(({ data }) => setHsOptions((p) => ({ ...p, [idx]: data.data.codes || [] })))
          .catch(() => setHsOptions((p) => ({ ...p, [idx]: [] })))
          .finally(() => setHsLoading((p) => ({ ...p, [idx]: false })));
      }, 1000);
    });

    return () => Object.values(timers).forEach(clearTimeout);
  }, [hsInputs, isAdmin]);

  const setItemHsCode = (idx, code) => {
    setForm((p) => {
      const items = [...p.items];
      items[idx] = {
        ...items[idx],
        hsCode: code?.codeWoSection || code?.code || "",
        hsCodeDescription: code?.description || "",
      };
      return { ...p, items };
    });
  };

  // 1. Debounced customer search 
  useEffect(() => {
    if (!isAdmin || !open) return;

    const query = customerInput.trim();
    if (query.length < 2) {
      setCustomerOptions([]);
      return;
    }

    setCustomerLoading(true);
    const timer = setTimeout(() => {
      userService
        .getAll({ role: "customer", search: query, limit: 20 })
        .then(({ data }) => setCustomerOptions(data.data || []))
        .catch(() => setCustomerOptions([]))
        .finally(() => setCustomerLoading(false));
    }, 1000);

    return () => clearTimeout(timer);
  }, [customerInput, isAdmin, open]);

  // 2. NEW — prefill selected customer when opening in edit mode
  useEffect(() => {
    if (!isAdmin || !open) return;

    if (isEdit && initialData?.customer) {
      setSelectedCustomer(initialData.customer);
      setCustomerInput(
        `${initialData.customer.firstName} ${initialData.customer.lastName}`,
      );
    }
  }, [isAdmin, isEdit, initialData, open]);

  // 3. Reset on close (existing)
  useEffect(() => {
    if (!open) {
      setSelectedCustomer(null);
      setCustomerOptions([]);
      setCustomerInput("");
    }
  }, [open]);

  const onDrop = useCallback((accepted, rejected) => {
    setPendingFiles((p) => [
      ...p,
      ...accepted.map((f) =>
        Object.assign(f, { preview: URL.createObjectURL(f) }),
      ),
    ]);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
      "image/webp": [".webp"],
    },
    maxSize: 10 * 1024 * 1024,
  });

  useEffect(() => {
    if (initialData) {
      setForm({
        vendorName: initialData.vendor?.name || "",
        vendorWebsite: initialData.vendor?.website || "",
        vendorCountry: initialData.vendor?.country || "USA",
        orderNumber: initialData.orderNumber || "",
        carrier: initialData.tracking?.carrier || initialData.carrier || "",
        providerTrackingNumber:
          initialData.tracking?.providerTrackingNumber || "",
        packageDescription: initialData.packageDescription || "",
        items:
          initialData.items?.length > 0
            ? initialData.items.map((i) => ({
              _id: i._id,
              description: i.description,
              trackingNumber: i.trackingNumber || "",
              quantity: i.quantity,
              hsCode: i.hsCode || "",
              hsCodeDescription: i.hsCodeDescription || "",
            }))
            : [{ ...EMPTY_ITEM }],
      });
      // Same "show the original figure, not the converted USD one" rule as
      // items above — re-saving unedited never re-converts.
      setInvoiceTotal(initialData.invoiceCurrency ? initialData.invoiceTotalOriginal : initialData.invoiceTotal || "");
      setInvoiceCurrency(initialData.invoiceCurrency || "USD");
      setTin(initialData.tin || "");
    } else {
      const draft = open ? loadDraft(currentUser?._id) : null;
      setForm(
        draft?.form || {
          vendorName: "",
          vendorWebsite: "",
          vendorCountry: "USA",
          orderNumber: "",
          carrier: "",
          providerTrackingNumber: "",
          packageDescription: "",
          items: [{ ...EMPTY_ITEM }],
        },
      );
      setInvoiceTotal(draft?.invoiceTotal ?? "");
      setTin(draft?.tin ?? "");
      setDocType(draft?.docType || "invoice");
    }
    setPendingFiles([]);
    if (isEdit) setDocType("invoice");
  }, [initialData, open]);

  // Autosave — every change to a NEW (not edit) shipment's form gets
  // written to localStorage, so navigating away (e.g. to Profile to add a
  // missing TIN) and coming back restores exactly where they left off.
  // Pending file uploads can't be serialized to localStorage, so those
  // deliberately aren't part of the draft — only the text/number fields.
  useEffect(() => {
    if (isEdit || !open) return;
    saveDraft(currentUser?._id, { form, invoiceTotal, tin, docType });
  }, [isEdit, open, form, invoiceTotal, tin, docType, currentUser?._id]);

  const set = (field) => (e) =>
    setForm((p) => ({ ...p, [field]: e.target.value }));

  const setItem = (idx, field) => (e) => {
    setForm((p) => {
      const items = [...p.items];
      items[idx] = { ...items[idx], [field]: e.target.value };
      return { ...p, items };
    });
  };

  const addItem = () =>
    setForm((p) => ({ ...p, items: [...p.items, { ...EMPTY_ITEM }] }));

  const removeItem = (idx) =>
    setForm((p) => ({
      ...p,
      items: p.items.filter((_, i) => i !== idx),
    }));

  const needsCustomerPicker = isAdmin;
  // Whichever account this shipment is being filed for — the picked
  // customer when an admin is creating it, or the logged-in customer
  // themselves otherwise.
  const tinOwner = isAdmin ? selectedCustomer : currentUser;
  // Admin explicitly picks a TIN per shipment (there's no "active TIN"
  // concept on their side). A customer instead always operates under
  // whichever TIN they've selected in the sidebar — no separate picker here,
  // it just follows `activeTin` — so this only ever renders for admin.
  const showTinPicker = isAdmin && !!tinOwner?.canManageMultipleTins && (tinOwner?.tins?.length > 0);
  const customerFollowsActiveTin =
    !isAdmin && !!tinOwner?.canManageMultipleTins && (tinOwner?.tins?.length > 0);

  useEffect(() => {
    if (isAdmin) {
      if (!showTinPicker) {
        setTin("");
        return;
      }
      // Preserve the existing TIN snapshot only while editing WITHOUT
      // reassigning — once the admin picks a different customer than the
      // shipment was originally filed under, the old TIN belongs to the
      // previous owner and must not carry over; fall through and default
      // to the new owner's own first TIN instead.
      const reassigningCustomer = isEdit && initialData?.customer?._id !== selectedCustomer?._id;
      if (isEdit && initialData?.tin && !reassigningCustomer) return;
      setTin(tinOwner?.tins?.[0]?.tin || "");
      return;
    }
    // Customer's own shipment: a new one always files under the currently
    // selected TIN. An existing shipment keeps whatever TIN it was already
    // filed under (set via initialData above) — it's a snapshot, not
    // something editing should be able to move between TINs.
    if (isEdit) return;
    setTin(customerFollowsActiveTin ? activeTin || "" : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showTinPicker, tinOwner, isAdmin, customerFollowsActiveTin, activeTin, isEdit, open]);

  const tinRequired = isAdmin ? showTinPicker : customerFollowsActiveTin && !isEdit;

  // Already-uploaded purchase invoice(s) — only present when editing, and
  // only if the shipment fetch that supplied `initialData` populated
  // `documents` (see shipmentController.getShipment(s)). Computed here (not
  // just below) so canSubmit can require at least one invoice on file.
  const existingInvoiceDocs = isEdit
    ? (initialData?.documents || []).filter((d) => d.type === "invoice")
    : [];

  const canSubmit =
    !!form.vendorName &&
    !!form.orderNumber &&
    !!form.vendorCountry &&
    !!docType &&
    (existingInvoiceDocs.length > 0 || pendingFiles.length > 0) &&
    !!invoiceTotal &&
    parseFloat(invoiceTotal) > 0 &&
    form.items.every((i) => i.description) &&
    !loading &&
    (!needsCustomerPicker || !!selectedCustomer) &&
    (!tinRequired || !!tin);

  const handleSubmit = () => {
    // Sent as typed, in whatever currency was selected — converted to USD
    // ONCE, server-side, at the moment this shipment is actually created
    // (using that day's live rate, see server/services/foreignCurrencyService.js),
    // never client-side ahead of time. That keeps the rate tied to the real
    // creation moment rather than whenever the form happened to be filled in.
    const payload = {
      vendor: {
        name: form.vendorName,
        website: form.vendorWebsite,
        country: form.vendorCountry,
      },
      orderNumber: form.orderNumber,
      carrier: form.carrier,
      packageDescription: form.packageDescription,
      invoiceTotal: parseFloat(invoiceTotal) || 0,
      invoiceCurrency,
      items: form.items
        .filter((i) => i.description)
        .map((i) => ({
          _id: i._id || undefined,
          description: i.description,
          trackingNumber: i.trackingNumber || undefined,
          quantity: parseInt(i.quantity) || 1,
          hsCode: i.hsCode || undefined,
          hsCodeDescription: i.hsCodeDescription || undefined,
        })),
    };
    if (needsCustomerPicker && selectedCustomer) {
      payload.customer = selectedCustomer._id;
    }
    if ((showTinPicker || customerFollowsActiveTin) && tin) {
      payload.tin = tin;
    }
    // "Attach To" removed from UI — files always attach at the shipment level now.
    if (!isEdit) clearDraft(currentUser?._id);
    onSubmit(payload, { pendingFiles, docType, attachTo: "shipment" });
  };

  const removePending = (idx) =>
    setPendingFiles((p) => p.filter((_, i) => i !== idx));

  // The dropzone below is purely for ADDING more invoice files, never a
  // replacement, so existingInvoiceDocs (computed above canSubmit) stays
  // visible alongside it rather than hiding once something's on file.
  const handleViewExistingDoc = async (doc) => {
    try {
      const { data } = await documentService.view(doc._id);
      window.open(data.data.url, "_blank");
    } catch {
      // Silently ignore — same as elsewhere in the app, this is a
      // best-effort "open in a new tab" action.
    }
  };

  // Every caller renders this inline as a plain page section now (see
  // AdminAddShipment/AdminEditShipment/CustomerAddShipment/
  // CustomerEditShipment.jsx) — the form used to also support rendering as
  // a modal Dialog, but that mode has no callers left, so the Dialog/
  // DialogTitle/DialogContent/DialogActions chrome was removed rather than
  // kept as dead, never-exercised code.
  const actionsProps = {
    sx: { display: "flex", justifyContent: "flex-end", gap: 1, px: 3, py: 2, borderTop: "1px solid", borderColor: "divider" },
  };

  return (
    <Box>
      <Box sx={{ p: 0 }}>
        {needsCustomerPicker && (
          <Box sx={{ p: { xs: 1.5, sm: 3 }, pb: 0 }}>
            <Typography
              variant="overline"
              color="text.secondary"
              display="block"
              mb={1.5}
            >
              {isEdit ? "Reassign Customer" : "Assign to Customer"}
            </Typography>
            {isEdit && (
              <Alert severity="warning" sx={{ mb: 1.5, borderRadius: 2 }}>
                Reassigning this shipment will not move past invoices, documents, or notifications to
                the new customer — those remain tied to the original customer's history.
              </Alert>
            )}
            <Autocomplete
              options={customerOptions}
              loading={customerLoading}
              value={selectedCustomer}
              onChange={(_, val) => setSelectedCustomer(val)}
              inputValue={customerInput}
              onInputChange={(_, val) => setCustomerInput(val)}
              filterOptions={(x) => x}
              getOptionLabel={(o) =>
                o ? `${o.firstName} ${o.lastName} (${o.email})` : ""
              }
              isOptionEqualToValue={(o, v) => o._id === v._id}
              noOptionsText={
                customerInput.trim().length < 2
                  ? "Type at least 2 characters…"
                  : customerLoading
                    ? "Searching…"
                    : "No customers found"
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select customer"
                  placeholder="Search by name, email, or TIN…"
                  slotProps={{
                    ...params.slotProps,
                    input: {
                      ...params.slotProps?.input,
                      endAdornment: (
                        <>
                          {customerLoading && <CircularProgress size={16} />}
                          {params.slotProps?.input?.endAdornment}
                        </>
                      ),
                    },
                  }}
                  required
                />
              )}
              sx={{ mb: 3 }}
            />

            <Divider />
          </Box>
        )}

        {showTinPicker && (
          <Box sx={{ p: { xs: 1.5, sm: 3 }, pb: 0 }}>
            <TextField
              fullWidth
              select
              label="File shipment under TIN"
              value={tin}
              onChange={(e) => setTin(e.target.value)}
            >
              {(tinOwner.tins || []).map((t) => (
                <MenuItem key={t.tin} value={t.tin}>
                  {t.label ? `${t.tin} (${t.label})` : t.tin}
                </MenuItem>
              ))}
            </TextField>
            <Divider sx={{ mt: 3 }} />
          </Box>
        )}

        {/* Section 1: Order & Vendor */}
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
          <SectionHeader
            icon={<Storefront sx={{ fontSize: 18 }} />}
            number={1}
            title="Order & Vendor"
            subtitle="Where this shipment is coming from and order details."
          />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 120px" },
              gap: 2,
              mb: 2,
            }}
          >
            <Autocomplete
              freeSolo
              options={vendorSuggestions}
              value={form.vendorName}
              inputValue={form.vendorName}
              onInputChange={(_, value) =>
                setForm((p) => ({ ...p, vendorName: value }))
              }
              onChange={(_, value) =>
                setForm((p) => ({ ...p, vendorName: value || "" }))
              }
              filterOptions={(options) => options}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Store / Vendor Name"
                  required
                  onBlur={handleVendorNameBlur}
                />
              )}
            />
            <TextField
              label="Website (optional)"
              value={form.vendorWebsite}
              onChange={set("vendorWebsite")}
            />
            <TextField
              label="Country"
              select
              value={form.vendorCountry}
              onChange={set("vendorCountry")}
              required
            >
              {["USA", "UK", "Canada", "China", "Other"].map((c) => (
                <MenuItem key={c} value={c}>
                  {c}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr" },
              gap: 2,
            }}
          >
            <TextField
              label="Order Number"
              value={form.orderNumber}
              onChange={set("orderNumber")}
              required
            />
          </Box>
        </Box>

        <Divider />

        {/* Section 2: Package & Invoice — left column is every field, right
            column is the invoice upload, 50/50 on sm+ (stacked on mobile). */}
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
          <SectionHeader
            icon={<Description sx={{ fontSize: 18 }} />}
            number={2}
            title="Package & Invoice"
            subtitle="Details about this package and its invoice."
          />

          {/* Package Description reads as this section's own headline
              (larger, bold label/text, same outlined box as every other
              field) rather than just another field among equals —
              everything below it (amount, doc type, upload) is a detail
              ABOUT the package it names. Wrapped in the SAME grid template
              (columns + gap) as the fields grid right below it, instead of
              a plain `width: 50%` of the section — that ignored the grid's
              own gap, so it came out slightly wider than the left column
              beneath it (amount/doc type) and the symmetry was off. */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
              mb: 3,
            }}
          >
            <TextField
              label="Package Description"
              value={form.packageDescription}
              onChange={set("packageDescription")}
              placeholder="e.g. Sony headphones"
              required
              sx={{
                // Slightly taller box to fit the larger 20px input text —
                // without this the box kept its normal "small" field
                // height and the text/label both sat off-center inside
                // it. Kept close to the other fields' own height rather
                // than a full "medium"-sized field, which looked
                // oversized next to them.
                "& .MuiOutlinedInput-root": { minHeight: 48 },
                "& .MuiOutlinedInput-input": { paddingTop: "12px", paddingBottom: "12px" },
                // The unfocused/empty label's default vertical offset is
                // a fixed pixel value tuned for a normal-height field —
                // with the taller box above, that left it sitting
                // noticeably above center instead of centered in the box.
                "& .MuiInputLabel-root:not(.MuiInputLabel-shrink)": {
                  top: "50%",
                  transform: "translate(14px, -50%) scale(1)",
                },
              }}
              slotProps={{
                input: {
                  sx: {
                    fontSize: 20,
                    fontWeight: 700,
                    // The placeholder inherits this input's font-weight/
                    // size but MUI's default placeholder color+opacity is
                    // tuned for small/regular text — at 20px/700 that
                    // combination read as barely-there instead of a bold
                    // headline prompt, so it's darkened and made fully
                    // opaque here.
                    "&::placeholder": { color: "text.secondary", opacity: 1 },
                  },
                },
                // Normal weight — only the actual typed-in text is the
                // bold headline; the field's own label stays regular
                // weight, just sized up slightly and darkened so it reads
                // clearly.
                inputLabel: { sx: { fontSize: 13, fontWeight: 400, color: "text.primary" } },
              }}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Total Invoice Amount"
                type="number"
                value={invoiceTotal}
                onChange={(e) => setInvoiceTotal(e.target.value)}
                required
                helperText={
                  invoiceCurrency === "USD"
                    ? "In USD, before taxes/duties"
                    : `Converted to USD automatically when the shipment is created (live rate), before taxes/duties`
                }
                inputProps={{ min: 0.01, step: 0.01 }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <Select
                        value={invoiceCurrency}
                        onChange={(e) => setInvoiceCurrency(e.target.value)}
                        variant="standard"
                        disableUnderline
                        renderValue={(c) => (
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <CurrencyFlag code={c} />
                            {c}
                          </Box>
                        )}
                        sx={{
                          mr: 0.75,
                          fontSize: 13,
                          fontWeight: 700,
                          bgcolor: "action.selected",
                          borderRadius: 1.5,
                          "& .MuiSelect-select": { py: 0.4, pl: 1, pr: "26px !important" },
                          "& .MuiSelect-icon": { color: "text.secondary" },
                        }}
                      >
                        {currencyOptions.map((c) => (
                          <MenuItem key={c} value={c} sx={{ fontSize: 13, display: "flex", gap: 1 }}>
                            <CurrencyFlag code={c} />
                            {c} — {getCurrencyCountry(c)?.countryName || "—"}
                          </MenuItem>
                        ))}
                      </Select>
                    ),
                    endAdornment: (
                      <Tooltip
                        title="Total amount shown on your purchase invoice, before any taxes or duties. Pick the currency it's actually billed in — it's converted to USD automatically using that day's live exchange rate when the shipment is created."
                        arrow
                      >
                        <InfoOutlined sx={{ fontSize: 19, color: "primary.main", cursor: "help" }} />
                      </Tooltip>
                    ),
                  },
                }}
              />
              <TextField
                select
                label="Doc Type"
                value={docType}
                onChange={(e) => setDocType(e.target.value)}
                required
              >
                <MenuItem value="invoice">Purchase Invoice</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </TextField>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              {existingInvoiceDocs.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                  <Typography variant="caption" sx={{ color: "success.dark", fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CheckCircle sx={{ fontSize: 14 }} /> Invoice uploaded
                  </Typography>
                  {existingInvoiceDocs.map((doc) => (
                    <Box
                      key={doc._id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        px: 1.25,
                        py: 0.75,
                        border: "1.5px solid",
                        borderColor: "success.main",
                        borderRadius: 1,
                        bgcolor: "success.light",
                      }}
                    >
                      <InsertDriveFile sx={{ fontSize: 18, color: "success.main" }} />
                      <Typography noWrap variant="body2" sx={{ flex: 1 }}>
                        {doc.originalName}
                      </Typography>
                      <IconButton size="small" onClick={() => handleViewExistingDoc(doc)} title="View">
                        <Visibility fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Darkened from "divider" (barely visible as an outline) so
                  the dropzone's own boundary is actually readable at a
                  glance, not just inferrable from the grey fill behind it. */}
              <Box
                {...getRootProps()}
                sx={{
                  border: "1.5px dashed",
                  borderColor: isDragActive ? "primary.main" : "grey.400",
                  borderRadius: 1.5,
                  py: 1.5,
                  px: 1.5,
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  gap: 0.25,
                  cursor: "pointer",
                  bgcolor: isDragActive ? "rgba(13,98,155,0.04)" : "action.hover",
                }}
                required={existingInvoiceDocs.length === 0}
              >
                <input {...getInputProps()} />
                <UploadFile sx={{ fontSize: 20, color: "text.disabled", mb: 0.5 }} />
                <Typography variant="body2" color="text.secondary">
                  {existingInvoiceDocs.length > 0 ? "Attach another file" : "Attach Purchase Invoice"}
                </Typography>
                <Typography variant="caption" color="text.disabled">
                  Drag &amp; drop or click to browse
                </Typography>
              </Box>

              {pendingFiles.length > 0 && (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  {pendingFiles.map((file, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        p: 1,
                        border: "1.5px solid",
                        borderColor: "success.main",
                        borderRadius: 1,
                        bgcolor: "success.light",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1, minWidth: 0 }}>
                        <CheckCircle sx={{ fontSize: 18, color: "success.main", flexShrink: 0 }} />
                        <Typography noWrap sx={{ mr: 2, flex: 1 }} variant="body2">
                          {file.name}
                        </Typography>
                      </Box>
                      <Button size="small" color="error" onClick={() => removePending(idx)}>
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Divider />

        {/* Section 3: Items / Contents */}
        <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              mb: 2.5,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <SectionHeader
              icon={<FormatListBulleted sx={{ fontSize: 18 }} />}
              number={3}
              title="Items / Contents"
              subtitle="Add the items included in this shipment."
            />
            <Button size="small" startIcon={<Add />} onClick={addItem}>
              Add Item
            </Button>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr" },
              mb: 2,
            }}
          >
            <TextField
              fullWidth
              select
              label="Carrier (optional)"
              value={form.carrier}
              onChange={set("carrier")}
            >
              {/* Values match server's normalizeCarrier() exactly (shipmentController.js)
                  — it upper-cases whatever's saved to one of these keys, so a shipment's
                  stored carrier (e.g. "FEDEX", "AMAZON") must match a MenuItem value here
                  or the Select silently shows nothing selected when editing. Labels are
                  just the friendlier display text. */}
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {CARRIER_OPTIONS.map(({ value, label }) => (
                <MenuItem key={value} value={value}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1 }}>
            {form.items.map((item, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr 1fr",
                    // Item Description takes half the row; Qty and
                    // Tracking # split the other half evenly (0.5fr each,
                    // same as half of Description's 1fr) — the delete
                    // icon stays a fixed width outside that split.
                    md: "1fr 0.5fr 0.5fr 36px",
                  },
                  gap: 1.5,
                  // "stretch" at xs/sm (tablet) used to stretch the delete
                  // IconButton to match the Qty field's full height,
                  // helper text included — making it look vertically
                  // misaligned against the actual input box next to it
                  // instead of centered on it. "flex-start" everywhere,
                  // same as the md layout already used, fixes that.
                  alignItems: "flex-start",
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "action.hover",
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >

                <TextField
                  size="small"
                  label="Item Description"
                  value={item.description}
                  onChange={setItem(idx, "description")}
                  placeholder="e.g. iPhone 15 Pro Max"
                  sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}
                  required
                />
                <TextField
                  size="small"
                  label="Item Tracking # (optional)"
                  value={item.trackingNumber}
                  onChange={setItem(idx, "trackingNumber")}
                  placeholder="Carrier/item tracking, if known"
                  sx={{ gridColumn: { xs: "1 / -1", md: "auto" } }}
                />
                <TextField
                  size="small"
                  label="Qty"
                  type="number"
                  value={item.quantity}
                  onChange={setItem(idx, "quantity")}
                  inputProps={{ min: 1 }}
                  helperText="Defaults to 1"
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeItem(idx)}
                  disabled={form.items.length === 1}
                  sx={{
                    mt: 0.5,
                    justifySelf: { xs: "end", md: "center" },
                    gridColumn: { xs: "2 / 3", md: "auto" },
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
                {isAdmin && (
                  <Autocomplete
                    size="small"
                    options={hsOptions[idx] || []}
                    loading={!!hsLoading[idx]}
                    value={item.hsCode ? { codeWoSection: item.hsCode, description: item.hsCodeDescription } : null}
                    onChange={(_, val) => setItemHsCode(idx, val)}
                    inputValue={hsInputs[idx] || ""}
                    onInputChange={(_, val) => setHsInputs((p) => ({ ...p, [idx]: val }))}
                    filterOptions={(x) => x}
                    getOptionLabel={(o) => (o ? `${o.codeWoSection || o.code} — ${o.description}` : "")}
                    isOptionEqualToValue={(o, v) => (o.codeWoSection || o.code) === (v.codeWoSection || v.code)}
                    noOptionsText={
                      (hsInputs[idx] || "").trim().length < 2 ? "Type at least 2 characters…" : "No HS codes found"
                    }
                    sx={{ gridColumn: { xs: "1 / -1", md: "1 / -1" } }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="HS Code (optional)"
                        placeholder="Search by product description…"
                        slotProps={{
                          ...params.slotProps,
                          input: {
                            ...params.slotProps?.input,
                            endAdornment: (
                              <>
                                {hsLoading[idx] && <CircularProgress size={14} />}
                                {params.slotProps?.input?.endAdornment}
                              </>
                            ),
                          },
                        }}
                      />
                    )}
                  />
                )}
              </Box>
            ))}
          </Box>

        </Box>
      </Box>

      <Box {...actionsProps}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit}>
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : isEdit ? (
            "Save Changes"
          ) : (
            "Create Shipment"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ShipmentForm;